import { useMemo } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { ErrorState } from '@/components/common/ErrorState';
import { FormSkeleton } from '@/components/common/FormSkeleton';
import { PageTitle } from '@/components/layout/PageTitle';
import { PageToolbar } from '@/components/layout/PageToolbar';
import { instituteDetailQueryOptions } from '../api/institutes.api';
import { InstituteForm } from '../components/InstituteForm';
import { useUpdateInstitute } from '../hooks/useUpdateInstitute';
import type { InstituteFormValues } from '../types/institute.types';
import {
  buildInstitutePayload,
  extractInstituteCascade,
  mapInstituteToFormValues,
} from '../utils/instituteFormMappers';

const INSTITUTE_FORM_FIELD_COUNT = 12;
const EDIT_INSTITUTE_TITLE = 'Edit Institute';

interface InstituteEditPageProps {
  instituteId: number;
}

export function InstituteEditPage({ instituteId }: InstituteEditPageProps) {
  const navigate = useNavigate();
  const { data: institute, isLoading, error } = useQuery(instituteDetailQueryOptions(instituteId));

  function goToList() {
    void navigate({ to: '/settings/institutes', search: (prev) => prev });
  }

  function goToListWithPending() {
    void navigate({
      to: '/settings/institutes',
      search: (prev) => ({ ...prev, _pendingId: instituteId }),
    });
  }

  const mutation = useUpdateInstitute(instituteId, goToListWithPending);

  function handleSubmit(values: InstituteFormValues) {
    mutation.mutate(buildInstitutePayload(values));
  }

  const defaultValues = useMemo(
    () => (institute ? mapInstituteToFormValues(institute) : null),
    [institute],
  );

  const cascade = useMemo(
    () => (institute ? extractInstituteCascade(institute) : null),
    [institute],
  );

  if (error) {
    return (
      <ErrorState
        error={error}
        fallbackTitle="Could not load this institute"
        className="mx-auto w-full max-w-4xl"
      />
    );
  }

  if (!isLoading && !defaultValues) {
    return null;
  }

  return (
    <div className="mx-auto flex w-full max-w-4xl flex-col gap-5">
      <PageTitle>{EDIT_INSTITUTE_TITLE}</PageTitle>
      <PageToolbar currentTitle={EDIT_INSTITUTE_TITLE} />
      {defaultValues ? (
        <InstituteForm
          defaultValues={defaultValues}
          initialDivisionId={cascade?.divisionId}
          initialDistrictId={cascade?.districtId}
          onSubmit={handleSubmit}
          onCancel={goToList}
          isPending={mutation.isPending}
          submitLabel="Save Changes"
          pendingLabel="Saving..."
        />
      ) : (
        <FormSkeleton fieldCount={INSTITUTE_FORM_FIELD_COUNT} />
      )}
    </div>
  );
}
